/* =============================================================================
   CORE / MENU-CATEGORIES — Rubriken-Hierarchie + Backfill-Mapping (S153)
   =============================================================================
   Pure-Functions ohne DOM/Storage:
     - kindForCategory: Rubrik-Name → kind ('menu'|'option'|'extra')
     - buildBackfillPlan: Spiegel der SQL-Backfill-Logik (phase_115) zum Testen
       (String → Top-/Unter-Rubrik + Item-Verknüpfung); rechnet KEINE md5-IDs,
       sondern arbeitet namensbasiert (die SQL-IDs sind deterministisch davon).
     - buildCategoryTree: flache Rubriken-Liste → top-down-Baum (Rubrik→Unter-Rubrik),
       sortiert nach kind (menu < option < extra) und sort_order. Für die UI.
============================================================================ */

/** Rubrik-Name → kind. Optionen→'option', Extras→'extra', sonst 'menu'. */
export function kindForCategory(name) {
  if (name === 'Optionen') return 'option';
  if (name === 'Extras') return 'extra';
  return 'menu';
}

const KIND_RANK = { menu: 0, option: 1, extra: 2 };

/**
 * Spiegel der SQL-Backfill-Logik (phase_115 §1.4) — namensbasiert, für Tests.
 * @param {Array<{id,category,subcategory,displayOrder,deletedAt}>} menuItems
 * @returns {{topLevels:Array, subLevels:Array, links:Array}}
 *   topLevels: [{name, kind, sortOrder}]  (je distinct category)
 *   subLevels: [{name, parentName, kind, sortOrder}] (je distinct category+subcategory)
 *   links:     [{itemId, categoryName, subcategoryName}] (lebende Items)
 */
export function buildBackfillPlan(menuItems) {
  const live = (Array.isArray(menuItems) ? menuItems : [])
    .filter(m => m && !m.deletedAt && m.category && String(m.category).trim() !== '');

  const topMap = new Map();   // category → min displayOrder
  const subMap = new Map();   // category|subcategory → {cat, sub, min}
  const links = [];

  for (const m of live) {
    const cat = String(m.category);
    const ord = Number(m.displayOrder || 0);
    if (!topMap.has(cat) || ord < topMap.get(cat)) topMap.set(cat, topMap.has(cat) ? Math.min(ord, topMap.get(cat)) : ord);

    const sub = (m.subcategory && String(m.subcategory).trim() !== '') ? String(m.subcategory) : null;
    if (sub) {
      const key = cat + '|' + sub;
      const prev = subMap.get(key);
      subMap.set(key, { cat, sub, min: prev ? Math.min(prev.min, ord) : ord });
    }
    links.push({ itemId: m.id, categoryName: cat, subcategoryName: sub });
  }

  const topLevels = [...topMap.entries()].map(([name, sortOrder]) => ({
    name, kind: kindForCategory(name), sortOrder
  }));
  const subLevels = [...subMap.values()].map(({ cat, sub, min }) => ({
    name: sub, parentName: cat, kind: kindForCategory(cat), sortOrder: min
  }));

  return { topLevels, subLevels, links };
}

/**
 * Flache Rubriken-Liste → top-down-Baum. Top-Level (parentId null) sortiert nach
 * kind (menu<option<extra) dann sort_order; je Top-Level seine Kinder nach sort_order.
 * @param {Array<{id,name,parentId,kind,sortOrder,active,icon}>} categories
 * @returns {Array<object & {children:Array}>}
 */
export function buildCategoryTree(categories) {
  const list = Array.isArray(categories) ? categories : [];
  const byParent = new Map();
  for (const c of list) {
    const p = c.parentId || null;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(c);
  }
  const sortSiblings = (arr, topLevel) => arr.slice().sort((a, b) => {
    if (topLevel) {
      const ra = KIND_RANK[a.kind] ?? 9, rb = KIND_RANK[b.kind] ?? 9;
      if (ra !== rb) return ra - rb;
    }
    if ((a.sortOrder || 0) !== (b.sortOrder || 0)) return (a.sortOrder || 0) - (b.sortOrder || 0);
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
  const tops = sortSiblings(byParent.get(null) || [], true);
  return tops.map(t => ({
    ...t,
    children: sortSiblings(byParent.get(t.id) || [], false)
  }));
}
